import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "sonner";

import {
  PDFEditor,
  type PDFEditorHandle,
  type PDFEditorPageOverlayProps,
  type PDFEditorToast,
  type PdfEditorFeatureFlags
} from "@/components/extend/pdf-editor";
import { cn } from "@/lib/utils";

import {
  bindToPdfFields,
  buildWidgetBindings,
  changedFieldValues,
  fieldBoxes,
  fieldsAtPoint,
  readPdfFormFields,
  widgetValuesForFields,
  type FieldBox,
  type FormCanvasField,
  type PdfFormField
} from "../formWidgets";
import { sortIntoReadingOrder } from "../overlayReadingOrder";
import { stringifyValue } from "../utils";

/** The editor in forms mode: filling only, no annotate, sign, redact or pages. */
const FORMS_ONLY: PdfEditorFeatureFlags = {
  annotate: false,
  redact: false,
  forms: true,
  sign: false,
  stamps: false,
  pages: false,
  security: false,
  print: false,
  capture: false,
  outline: false,
  attachments: false,
  comments: false,
  fullscreen: false
};

/** A field box on a rendered page, in CSS pixels from the page's top-left. */
export type FormCanvasRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type FormCanvasFieldOverlayProps = {
  field: FormCanvasField;
  box: FieldBox;
  boxIndex: number;
  rect: FormCanvasRect;
  pageNumber: number;
  scale: number;
};

export type FormCanvasPageOverlayProps = {
  pageNumber: number;
  pageWidth: number;
  pageHeight: number;
  scale: number;
  /** The fields drawn on this page, in reading order. */
  fields: FormCanvasField[];
};

/** The field under the pointer, for hover cards and click-to-select. */
export type FormCanvasFieldTarget = {
  field: FormCanvasField;
  pageNumber: number;
  box: FieldBox;
  /** The field's box on screen, for anchoring a popover. */
  clientRect: DOMRect;
};

export type FormCanvasHandle = {
  /** The PDF as filled in the editor, for saving. */
  getDocumentBuffer: () => Promise<ArrayBuffer>;
  /** Scroll the field's first box into the middle of the viewport. */
  scrollToField: (fieldName: string) => void;
  /** Scroll an area of a page (PDF points, top-left origin) into view. */
  scrollToPageArea: (pageNumber: number, box: FormCanvasRect) => void;
  /** Scroll to the field and put the caret in its input. */
  focusField: (fieldName: string) => void;
  /** The field's first box on screen now, or null while its page isn't rendered. */
  getFieldClientRect: (fieldName: string) => DOMRect | null;
  /** Whether the field's value is written into one of the PDF's own widgets. */
  fillsWidget: (fieldName: string) => boolean;
};

type FormCanvasProps = {
  /** The unflattened working PDF; flattened copies have no widgets to fill. */
  file: Blob;
  fileName: string;
  fields: FormCanvasField[];
  /** The Extend values the loaded PDF was filled with. */
  pdfValues: Record<string, unknown>;
  /** The current (possibly unsaved) Extend values. */
  values: Record<string, unknown>;
  /** Extend values the user changed in the PDF, to merge into the dirty state. */
  onValuesChange: (changed: Record<string, unknown>) => void;
  selectedFieldName?: string | null;
  /** Fields the Needs Input list asks for; highlighted while empty. */
  needsInputFieldNames?: ReadonlySet<string>;
  /** Controls at the end of the viewer bar (download, sources). */
  toolbarActions?: React.ReactNode;
  /** Extension point: drawn over each field box (markers, pins). */
  renderFieldOverlay?: (props: FormCanvasFieldOverlayProps) => React.ReactNode;
  /** Extension point: drawn once over each page. */
  renderPageOverlay?: (props: FormCanvasPageOverlayProps) => React.ReactNode;
  onFieldHover?: (target: FormCanvasFieldTarget | null) => void;
  onFieldClick?: (target: FormCanvasFieldTarget) => void;
  /** A field's input took focus; `keyboard` is false when a click put it there. */
  onFieldFocus?: (
    target: FormCanvasFieldTarget,
    via: { keyboard: boolean }
  ) => void;
  onLoadError?: (message: string) => void;
  className?: string;
};

const notify = ({ message, tone }: PDFEditorToast) => {
  if (tone === "error") toast.error(message);
  else if (tone === "success") toast.success(message);
  else toast(message);
};

const isEmptyValue = (value: unknown) =>
  value == null || value === false || stringifyValue(value).trim() === "";

/** Marks a fallback input with the Extend field it edits. */
const FIELD_ATTRIBUTE = "data-form-field";
/** A focus this soon after a pointer press came from the pointer. */
const POINTER_FOCUS_MS = 500;

const attributeSelector = (attribute: string, value: string) =>
  `[${attribute}="${value.replace(/["\\]/g, "\\$&")}"]`;

/** A box on a page element in viewport pixels; the overlay sits inside the page's border. */
function boxClientRect(
  pageElement: HTMLElement,
  scale: number,
  box: FieldBox
): DOMRect {
  const pageRect = pageElement.getBoundingClientRect();
  return new DOMRect(
    pageRect.left + pageElement.clientLeft + box.left * scale,
    pageRect.top + pageElement.clientTop + box.top * scale,
    box.width * scale,
    box.height * scale
  );
}

export const FormCanvas = forwardRef<FormCanvasHandle, FormCanvasProps>(
  function FormCanvas(
    {
      file,
      fileName,
      fields,
      pdfValues,
      values,
      onValuesChange,
      selectedFieldName,
      needsInputFieldNames,
      toolbarActions,
      renderFieldOverlay,
      renderPageOverlay,
      onFieldHover,
      onFieldClick,
      onFieldFocus,
      onLoadError,
      className
    },
    ref
  ) {
    const editorRef = useRef<PDFEditorHandle>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const lastPointerDownRef = useRef(0);
    // The widgets the loaded PDF actually has; a field with no widget in it
    // (a flattened copy) falls back to an HTML input.
    const [pdfWidgetNames, setPdfWidgetNames] = useState<Set<string> | null>(
      null
    );
    const [pdfFields, setPdfFields] = useState<Map<
      string,
      PdfFormField
    > | null>(null);
    useEffect(() => {
      let current = true;
      setPdfFields(null);
      void readPdfFormFields(file).then((read) => {
        if (current) setPdfFields(read);
      });
      return () => {
        current = false;
      };
    }, [file]);
    const bindings = useMemo(
      () =>
        buildWidgetBindings(
          pdfFields
            ? bindToPdfFields(fields, pdfFields)
            : pdfWidgetNames
            ? fields.map((field) =>
                (field.widget_names ?? []).every(
                  (name) => !name || pdfWidgetNames.has(name)
                )
                  ? field
                  : { ...field, widget_names: null }
              )
            : fields
        ),
      [fields, pdfFields, pdfWidgetNames]
    );
    const fieldsByName = useMemo(
      () => new Map(fields.map((field) => [field.field_name, field])),
      [fields]
    );
    const fieldsByPage = useMemo(() => {
      const byPage = new Map<number, FormCanvasField[]>();
      for (const field of fields) {
        const pages = new Set(fieldBoxes(field).map((box) => box.pageIndex));
        for (const pageIndex of pages) {
          const list = byPage.get(pageIndex) ?? [];
          list.push(field);
          byPage.set(pageIndex, list);
        }
      }
      for (const [pageIndex, list] of byPage) {
        byPage.set(
          pageIndex,
          sortIntoReadingOrder(list, (field) => {
            const box = fieldBoxes(field).find(
              (candidate) => candidate.pageIndex === pageIndex
            );
            return { top: box?.top ?? 0, left: box?.left ?? 0 };
          })
        );
      }
      return byPage;
    }, [fields]);

    // What the editor's widgets held at the last event, and the Extend values
    // the PDF holds; pushing a value from outside the editor (the Needs Input
    // list) is the difference between `values` and the latter.
    const widgetSnapshotRef = useRef<Record<string, string> | null>(null);
    const pdfHoldsRef = useRef<Record<string, unknown>>(pdfValues);
    const pushingRef = useRef(0);
    const valuesRef = useRef(values);
    const onValuesChangeRef = useRef(onValuesChange);
    const pageScalesRef = useRef(new Map<number, number>());
    const hoveredRef = useRef<string | null>(null);
    const [formReady, setFormReady] = useState(false);

    useEffect(() => {
      valuesRef.current = values;
      onValuesChangeRef.current = onValuesChange;
    });

    useEffect(() => {
      setFormReady(false);
      setPdfWidgetNames(null);
      widgetSnapshotRef.current = null;
      pdfHoldsRef.current = pdfValues;
      // Only a new file resets what the PDF holds.
    }, [file]);

    const handleFormReady = useCallback(
      (widgetValues: Record<string, string>) => {
        widgetSnapshotRef.current = widgetValues;
        setPdfWidgetNames(new Set(Object.keys(widgetValues)));
        setFormReady(true);
      },
      []
    );

    const handleFormValuesChange = useCallback(
      (widgetValues: Record<string, string>) => {
        const previous = widgetSnapshotRef.current;
        widgetSnapshotRef.current = widgetValues;
        // An echo of our own push: the Extend value stays as typed even when
        // the widget's limit cut it, so Needs Input still reports it.
        if (!previous || pushingRef.current > 0) return;
        const changed = changedFieldValues(bindings, previous, widgetValues);
        if (Object.keys(changed).length === 0) return;
        pdfHoldsRef.current = { ...pdfHoldsRef.current, ...changed };
        onValuesChangeRef.current(changed);
      },
      [bindings]
    );

    useEffect(() => {
      if (!formReady) return;
      const pending: Record<string, unknown> = {};
      for (const fieldName of bindings.byField.keys()) {
        if (
          stringifyValue(values[fieldName]) !==
          stringifyValue(pdfHoldsRef.current[fieldName])
        ) {
          pending[fieldName] = values[fieldName];
        }
      }
      if (Object.keys(pending).length === 0) return;
      pdfHoldsRef.current = { ...pdfHoldsRef.current, ...pending };
      const editor = editorRef.current;
      if (!editor) return;
      pushingRef.current += 1;
      editor
        .setFormValues(widgetValuesForFields(bindings, pending))
        .catch(() => undefined)
        .finally(() => {
          widgetSnapshotRef.current = editor.getFormValues();
          pushingRef.current -= 1;
        });
    }, [bindings, formReady, values]);

    const scrollToPageArea = useCallback(
      (pageNumber: number, box: FormCanvasRect) => {
        editorRef.current?.scrollToPageArea(
          pageNumber,
          { x: box.left, y: box.top, width: box.width, height: box.height },
          { smooth: true }
        );
      },
      []
    );

    const pageElementFor = useCallback((pageNumber: number) => {
      const element = rootRef.current?.querySelector(
        attributeSelector("data-pdf-editor-page", String(pageNumber))
      );
      return element instanceof HTMLElement ? element : null;
    }, []);

    /** The input that edits a field: its first widget, else its fallback. */
    const inputFor = useCallback(
      (fieldName: string) => {
        const [binding] = bindings.byField.get(fieldName) ?? [];
        const selector = binding
          ? attributeSelector("name", binding.widgetName)
          : attributeSelector(FIELD_ATTRIBUTE, fieldName);
        const element = rootRef.current?.querySelector(selector);
        return element instanceof HTMLElement ? element : null;
      },
      [bindings]
    );

    useImperativeHandle(
      ref,
      () => ({
        getDocumentBuffer: () =>
          editorRef.current?.getDocumentBuffer() ??
          Promise.reject(new Error("The form is not loaded yet.")),
        scrollToField: (fieldName: string) => {
          const field = fieldsByName.get(fieldName);
          const [box] = field ? fieldBoxes(field) : [];
          if (box) scrollToPageArea(box.pageIndex + 1, box);
        },
        scrollToPageArea,
        focusField: (fieldName: string) => {
          const field = fieldsByName.get(fieldName);
          const [box] = field ? fieldBoxes(field) : [];
          if (!box) return;
          scrollToPageArea(box.pageIndex + 1, box);
          // The page may only render once the scroll reaches it.
          let frames = 0;
          const attempt = () => {
            const input = inputFor(fieldName);
            if (input) {
              input.focus({ preventScroll: true });
              return;
            }
            frames += 1;
            if (frames < 30) requestAnimationFrame(attempt);
          };
          requestAnimationFrame(attempt);
        },
        getFieldClientRect: (fieldName: string) => {
          const field = fieldsByName.get(fieldName);
          const [box] = field ? fieldBoxes(field) : [];
          if (!box) return null;
          const pageNumber = box.pageIndex + 1;
          const scale = pageScalesRef.current.get(pageNumber);
          const pageElement = pageElementFor(pageNumber);
          return scale && pageElement
            ? boxClientRect(pageElement, scale, box)
            : null;
        },
        fillsWidget: (fieldName: string) => bindings.byField.has(fieldName)
      }),
      [bindings, fieldsByName, inputFor, pageElementFor, scrollToPageArea]
    );

    const targetAt = useCallback(
      (event: React.PointerEvent | React.MouseEvent) => {
        const target = event.target as Element | null;
        const pageElement = target?.closest?.("[data-pdf-editor-page]");
        if (!(pageElement instanceof HTMLElement)) return null;
        const pageNumber = Number(pageElement.dataset.pdfEditorPage);
        const scale = pageScalesRef.current.get(pageNumber);
        if (!pageNumber || !scale) return null;
        const pageRect = pageElement.getBoundingClientRect();
        const x =
          (event.clientX - pageRect.left - pageElement.clientLeft) / scale;
        const y =
          (event.clientY - pageRect.top - pageElement.clientTop) / scale;
        const [field] = fieldsAtPoint(
          fieldsByPage.get(pageNumber - 1) ?? [],
          pageNumber - 1,
          x,
          y
        );
        if (!field) return null;
        const box =
          fieldBoxes(field).find(
            (candidate) =>
              candidate.pageIndex === pageNumber - 1 &&
              x >= candidate.left &&
              x <= candidate.left + candidate.width &&
              y >= candidate.top &&
              y <= candidate.top + candidate.height
          ) ?? fieldBoxes(field)[0];
        return {
          field,
          pageNumber,
          box,
          clientRect: boxClientRect(pageElement, scale, box)
        } satisfies FormCanvasFieldTarget;
      },
      [fieldsByPage]
    );

    /** The field a focused input edits: a widget by its name, a fallback by its mark. */
    const fieldForInput = (element: Element): FormCanvasField | null => {
      const fallback = element.getAttribute(FIELD_ATTRIBUTE);
      if (fallback) return fieldsByName.get(fallback) ?? null;
      const widgetName = element.getAttribute("name");
      const [binding] = widgetName
        ? bindings.byWidget.get(widgetName) ?? []
        : [];
      return binding ? fieldsByName.get(binding.fieldName) ?? null : null;
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      if (!onFieldFocus || !(event.target instanceof Element)) return;
      const field = fieldForInput(event.target);
      const [box] = field ? fieldBoxes(field) : [];
      if (!field || !box) return;
      onFieldFocus(
        {
          field,
          pageNumber: box.pageIndex + 1,
          box,
          clientRect: event.target.getBoundingClientRect()
        },
        {
          keyboard:
            performance.now() - lastPointerDownRef.current > POINTER_FOCUS_MS
        }
      );
    };

    // Native widgets take the pointer, so hover comes from the field rectangles.
    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
      if (!onFieldHover) return;
      const target = targetAt(event);
      const name = target?.field.field_name ?? null;
      if (name === hoveredRef.current) return;
      hoveredRef.current = name;
      onFieldHover(target);
    };

    const handlePointerLeave = () => {
      if (!onFieldHover || hoveredRef.current === null) return;
      hoveredRef.current = null;
      onFieldHover(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onFieldClick) return;
      const target = targetAt(event);
      if (target) onFieldClick(target);
    };

    const setFallbackValue = useCallback(
      (fieldName: string, value: unknown) => {
        onValuesChangeRef.current({ [fieldName]: value });
      },
      []
    );

    // The overlay reads only the fallback inputs' values and which Needs Input
    // fields are empty, so typing into a native widget does not redraw pages.
    const fallbackKey = JSON.stringify(
      [...bindings.unmatched].map((name) => [
        name,
        stringifyValue(values[name])
      ])
    );
    const fallbackValues = useMemo(
      () =>
        new Map<string, unknown>(
          [...bindings.unmatched].map((name) => [name, values[name]])
        ),
      [bindings, fallbackKey]
    );
    const emptyNeedsKey = [...(needsInputFieldNames ?? [])]
      .filter((name) => isEmptyValue(values[name]))
      .join("\n");
    const emptyNeedsInput = useMemo(
      () => new Set(emptyNeedsKey ? emptyNeedsKey.split("\n") : []),
      [emptyNeedsKey]
    );

    const renderOverlay = useCallback(
      ({
        pageNumber,
        pageWidth,
        pageHeight,
        scale
      }: PDFEditorPageOverlayProps) => {
        pageScalesRef.current.set(pageNumber, scale);
        const pageIndex = pageNumber - 1;
        const pageFields = fieldsByPage.get(pageIndex) ?? [];
        const toRect = (box: FieldBox): FormCanvasRect => ({
          left: box.left * scale,
          top: box.top * scale,
          width: box.width * scale,
          height: box.height * scale
        });
        return (
          <div
            data-slot="form-canvas-overlay"
            className="pointer-events-none absolute inset-0 z-10"
          >
            {pageFields.flatMap((field) =>
              fieldBoxes(field).map((box, boxIndex) => {
                if (box.pageIndex !== pageIndex) return null;
                const rect = toRect(box);
                const key = `${field.field_name}-${boxIndex}`;
                const fallback = bindings.unmatched.has(field.field_name);
                const needsInput = emptyNeedsInput.has(field.field_name);
                const selected = selectedFieldName === field.field_name;
                return (
                  <React.Fragment key={key}>
                    {fallback ? (
                      <FallbackFieldInput
                        field={field}
                        boxIndex={boxIndex}
                        rect={rect}
                        scale={scale}
                        value={fallbackValues.get(field.field_name)}
                        invalid={needsInput}
                        onChange={setFallbackValue}
                      />
                    ) : null}
                    {selected || needsInput ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          "absolute rounded-xs ring-1",
                          selected ? "ring-accent-seed" : "ring-warn"
                        )}
                        style={{
                          left: rect.left - 2,
                          top: rect.top - 2,
                          width: rect.width + 4,
                          height: rect.height + 4
                        }}
                      />
                    ) : null}
                    {renderFieldOverlay?.({
                      field,
                      box,
                      boxIndex,
                      rect,
                      pageNumber,
                      scale
                    })}
                  </React.Fragment>
                );
              })
            )}
            {renderPageOverlay?.({
              pageNumber,
              pageWidth,
              pageHeight,
              scale,
              fields: pageFields
            })}
          </div>
        );
      },
      [
        bindings,
        emptyNeedsInput,
        fallbackValues,
        fieldsByPage,
        renderFieldOverlay,
        renderPageOverlay,
        selectedFieldName,
        setFallbackValue
      ]
    );

    return (
      <div
        ref={rootRef}
        className={cn("h-full min-h-0 w-full", className)}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onPointerDownCapture={() => {
          lastPointerDownRef.current = performance.now();
        }}
        onClick={handleClick}
        onFocus={handleFocus}
      >
        <PDFEditor
          ref={editorRef}
          className="h-full"
          src={file}
          fileName={fileName}
          defaultMode="forms"
          defaultZoom={1}
          features={FORMS_ONLY}
          toolbarVariant="forms"
          showUpload={false}
          showDownload={false}
          toolbarActions={toolbarActions}
          persistSignatures={false}
          signatureFontsStylesheetUrl={null}
          renderPageOverlay={renderOverlay}
          onFormReady={handleFormReady}
          onFormValuesChange={handleFormValuesChange}
          onDocumentLoadError={onLoadError}
          onToast={notify}
        />
      </div>
    );
  }
);

/**
 * The HTML input kept for a field the PDF has no matched widget for, so the
 * field stays editable while the match rate is below 100%.
 */
function FallbackFieldInput({
  field,
  boxIndex,
  rect,
  scale,
  value,
  invalid,
  onChange
}: {
  field: FormCanvasField;
  boxIndex: number;
  rect: FormCanvasRect;
  scale: number;
  value: unknown;
  invalid: boolean;
  onChange: (fieldName: string, value: unknown) => void;
}) {
  const fieldType = stringifyValue(field.field_type).trim().toLowerCase();
  const id = `form-canvas-fallback-${field.field_name}-${boxIndex}`;
  const style: React.CSSProperties = {
    left: rect.left,
    top: rect.top,
    width: Math.max(10, rect.width),
    height: Math.max(10, rect.height),
    fontSize: Math.max(8, Math.min(13, scale * 10))
  };
  const stop = (event: React.PointerEvent) => event.stopPropagation();
  const className =
    "pointer-events-auto absolute rounded-xs border border-accent-seed/70 bg-elevated text-fg outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40";

  if (fieldType === "checkbox") {
    const checked =
      typeof value === "boolean"
        ? value
        : ["true", "yes", "1"].includes(
            stringifyValue(value).trim().toLowerCase()
          );
    return (
      <label
        htmlFor={id}
        aria-label={field.question}
        title={field.question}
        className={cn(className, "flex items-center justify-center")}
        style={style}
        onPointerDown={stop}
      >
        <input
          id={id}
          data-form-field={field.field_name}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(field.field_name, event.target.checked)}
          className="h-full w-full cursor-pointer accent-primary"
        />
      </label>
    );
  }

  if ((field.options ?? []).length > 0) {
    return (
      <select
        id={id}
        data-form-field={field.field_name}
        aria-label={field.question}
        title={field.question}
        value={stringifyValue(value)}
        onChange={(event) => onChange(field.field_name, event.target.value)}
        className={cn(className, "px-1")}
        style={style}
        onPointerDown={stop}
      >
        <option value="" aria-label="No selection" />
        {(field.options ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  const maxLength = field.max_length ?? undefined;
  const text = stringifyValue(value);
  const tooLong = Boolean(maxLength && text.length > maxLength);
  const common = {
    id,
    "data-form-field": field.field_name,
    "aria-label": field.question,
    "aria-invalid": invalid || tooLong ? true : undefined,
    title: tooLong
      ? `${field.question} (too long: the printed field holds ${maxLength} characters)`
      : field.question,
    maxLength,
    value: text,
    onPointerDown: stop,
    style,
    className: cn(
      className,
      "px-1 leading-none",
      tooLong && "border-danger ring-1 ring-danger"
    )
  };
  if (rect.height > 28) {
    return (
      <textarea
        {...common}
        onChange={(event) => onChange(field.field_name, event.target.value)}
      />
    );
  }
  return (
    <input
      {...common}
      onChange={(event) => onChange(field.field_name, event.target.value)}
    />
  );
}
